import type { FrameTime, ListEntry, PropertyDescriptor, SelectOption, SourceContext, SourceStatusDetail, SourceTypeDefinition } from '../core/definitions';
import type { SettingsBag } from '../core/model';
import { BaseSourceRuntime, num, str } from './base';
import { acquireFontAsset, missingFontWarning, type FontAssetHandle } from './fonts';
import { rasterRatio } from './graphics';
import {
  countdownFinished,
  countdownFromBag,
  countdownRemaining,
  countdownToBag,
  formatClock,
  formatDuration,
  formatShowsSeconds,
  isValidLocale,
  isValidTimeZone,
  listTimeZones,
  pauseCountdown,
  resetCountdown,
  startCountdown,
  type CountdownState,
} from './timeFormat';
import {
  MAX_RASTER_EDGE,
  TEXT_STYLE_DEFAULTS,
  drawText,
  ensureFont,
  rasterFit,
  readTextStyle,
  textStyleColourProblem,
  textStyleProperties,
  type TextRaster,
  type TextStyle,
} from './textRender';

type WarningKey = 'font' | 'raster' | 'zone' | 'locale';

/**
 * Shared by every text-drawing source: font files from project assets, the
 * "font not available" check, raster scale, and the non-fatal status warnings
 * (several causes can hold at once; the status shows them together).
 */
abstract class TextSourceRuntime<S extends SettingsBag> extends BaseSourceRuntime<S> {
  protected style: TextStyle = { ...TEXT_STYLE_DEFAULTS };
  protected ratio = 1;
  protected showing = false;
  private readonly warnings = new Map<WarningKey, string>();
  private fontAssetId = '';
  private fontHandle: FontAssetHandle | null = null;
  private fontFamily: string | null = null;

  /** Bridge until the properties UI shows `status.warning`; `rasterScale` is the ratio drawn at. */
  getLiveInfo(): Record<string, string> {
    return { warning: this.getStatus().warning ?? '', rasterScale: String(this.ratio) };
  }

  setShowing(showing: boolean): void {
    this.showing = showing;
  }

  setRasterScale(scale: number): void {
    const ratio = rasterRatio(scale);
    if (this.destroyed || ratio === this.ratio) return;
    this.ratio = ratio;
    this.rerender();
  }

  /** Redraws with the current settings (font arrived, raster scale changed). */
  protected abstract rerender(): void;

  protected noteWarning(key: WarningKey, message: string | null): void {
    if ((this.warnings.get(key) ?? null) === message) return;
    if (message === null) this.warnings.delete(key);
    else this.warnings.set(key, message);
    this.setWarning(this.warnings.size ? [...this.warnings.values()].join(' ') : null);
  }

  /** The style to draw with: the asset font first once it has loaded, the named family as fallback. */
  protected drawStyle(): TextStyle {
    return this.fontFamily ? { ...this.style, fontFamily: `"${this.fontFamily}", ${this.style.fontFamily}` } : this.style;
  }

  /** Applies style and font settings; redraws once more when a font finishes loading. */
  protected applyStyle(settings: SettingsBag, sampleText: string): void {
    this.style = readTextStyle(settings);
    const assetId = str(settings.fontAssetId, '');
    if (assetId !== this.fontAssetId) {
      this.fontHandle?.release();
      this.fontHandle = null;
      this.fontFamily = null;
      this.fontAssetId = assetId;
      if (assetId) {
        const handle = acquireFontAsset(this.ctx.assets, assetId);
        this.fontHandle = handle;
        handle.ready.then(
          () => {
            if (this.fontHandle !== handle) return;
            this.fontFamily = handle.family;
            this.noteWarning('font', null);
            this.rerender();
          },
          (err: unknown) => {
            if (this.fontHandle !== handle) return;
            this.noteWarning('font', `Font file could not be used: ${err instanceof Error ? err.message : String(err)}. Showing "${this.style.fontFamily}".`);
          },
        );
      }
    }
    if (!assetId) this.noteWarning('font', null);
    const epoch = this.beginEpoch();
    const style = this.style;
    void ensureFont(style, sampleText).then((late) => {
      if (!this.isCurrent(epoch)) return;
      if (!this.fontHandle) this.noteWarning('font', missingFontWarning(style.fontFamily));
      if (late) this.rerender();
    });
  }

  /** Draws `text` into `canvas` at the raster ratio; null when a colour is invalid (status already set). */
  protected render(canvas: HTMLCanvasElement, text: string, overrides: Partial<TextStyle> = {}): TextRaster | null {
    const style = { ...this.drawStyle(), ...overrides };
    const problem = textStyleColourProblem(style);
    if (problem) {
      this.clearVideo();
      this.fail('unknown', problem);
      return null;
    }
    const raster = drawText(canvas, text, style, this.ratio);
    this.noteWarning('raster',
      raster.pixelRatio < 1
        ? `The text is larger than the ${MAX_RASTER_EDGE} px texture limit and is drawn at ${Math.round(raster.pixelRatio * 100)}% resolution.`
        : null,
    );
    return raster;
  }

  protected release(): void {
    this.fontHandle?.release();
    this.fontHandle = null;
  }
}

/**
 * One block of styled text on a canvas: draws on demand, skips identical
 * redraws, and only bumps the texture version when the size is unchanged.
 */
abstract class StyledTextRuntime<S extends SettingsBag> extends TextSourceRuntime<S> {
  protected readonly canvas = document.createElement('canvas');
  /** The text on air; null while hidden or before the first draw. */
  protected shown: string | null = null;
  private hidden = false;
  private size = { width: 0, height: 0 };

  /** Sub-state reported with 'live' (paused, stopped). */
  protected liveDetail(): SourceStatusDetail | undefined {
    return undefined;
  }

  /** Shows `text`, or nothing at all for null. */
  protected show(text: string | null, opts: { force?: boolean; fromTick?: boolean } = {}): void {
    if (this.destroyed) return;
    if (text === null) {
      if (!this.hidden || opts.force) {
        this.hidden = true;
        this.shown = null;
        this.clearVideo();
      }
      this.setStatus('live', undefined, this.liveDetail());
      return;
    }
    if (!opts.force && !this.hidden && text === this.shown && this.getVideo()) {
      this.setStatus('live', undefined, this.liveDetail());
      return;
    }
    const { width: cw, height: ch } = this.canvas;
    const size = this.render(this.canvas, text);
    if (!size) {
      this.shown = null;
      return;
    }
    this.shown = text;
    this.hidden = false;
    const sameSurface = size.width === this.size.width && size.height === this.size.height && cw === this.canvas.width && ch === this.canvas.height;
    this.size = size;
    if (opts.fromTick && sameSurface && this.getVideo()) this.touchVideo();
    else this.publishVideo(this.canvas, size.width, size.height, false, size.pixelRatio);
    this.setStatus('live', undefined, this.liveDetail());
  }

  protected override release(): void {
    super.release();
    this.canvas.width = 0;
    this.canvas.height = 0;
  }
}

// ---------------------------------------------------------------------------
// text
// ---------------------------------------------------------------------------

export type TextSettings = SettingsBag & { text: string };

class TextRuntime extends StyledTextRuntime<TextSettings> {
  private text = '';

  constructor(ctx: SourceContext, settings: TextSettings) {
    super(ctx);
    this.apply(settings);
  }

  update(settings: TextSettings): void {
    if (!this.destroyed) this.apply(settings);
  }

  protected rerender(): void {
    this.show(this.text, { force: true });
  }

  private apply(settings: TextSettings): void {
    this.text = str(settings.text, 'Text');
    this.applyStyle(settings, this.text);
    this.show(this.text, { force: true });
  }
}

export const textDefinition: SourceTypeDefinition<TextSettings> = {
  kind: 'text',
  displayName: 'Text',
  caps: { video: true, audio: false },
  category: 'graphics',
  defaults: () => ({ ...TEXT_STYLE_DEFAULTS, fontAssetId: '', text: 'Text' }),
  properties: (): PropertyDescriptor[] => [{ key: 'text', label: 'Text', type: 'text', multiline: true }, ...textStyleProperties()],
  create: (ctx, settings) => new TextRuntime(ctx, settings),
};

// ---------------------------------------------------------------------------
// clock
// ---------------------------------------------------------------------------

export const CLOCK_FORMAT_PRESETS: SelectOption[] = [
  { value: 'HH:mm:ss', label: '24-hour (14:05:09)' },
  { value: 'HH:mm', label: '24-hour, no seconds (14:05)' },
  { value: 'h:mm:ss A', label: '12-hour (2:05:09 PM)' },
  { value: 'h:mm A', label: '12-hour, no seconds (2:05 PM)' },
  { value: 'dddd D MMMM YYYY', label: 'Date (Friday 9 October 2026)' },
  { value: 'DD/MM/YYYY', label: 'Short date (09/10/2026)' },
  { value: 'dddd D MMMM HH:mm:ss', label: 'Date and 24-hour time' },
  { value: 'ddd D MMM h:mm A', label: 'Date and 12-hour time' },
  { value: 'custom', label: 'Custom format' },
];

const LOCALES = [
  'en-GB', 'en-US', 'es-ES', 'es-MX', 'ca-ES', 'eu-ES', 'gl-ES', 'pt-PT', 'pt-BR', 'fr-FR', 'de-DE', 'it-IT', 'nl-NL', 'pl-PL',
  'sv-SE', 'da-DK', 'nb-NO', 'fi-FI', 'cs-CZ', 'el-GR', 'ro-RO', 'hu-HU', 'tr-TR', 'ru-RU', 'uk-UA', 'ar-SA', 'he-IL', 'hi-IN',
  'ja-JP', 'ko-KR', 'zh-CN', 'zh-TW', 'th-TH', 'vi-VN', 'id-ID',
];

function localeOptions(): SelectOption[] {
  const extra = typeof navigator !== 'undefined' && navigator.language && !LOCALES.includes(navigator.language) ? [navigator.language] : [];
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames(undefined, { type: 'language' });
  } catch {
    names = null;
  }
  return [
    { value: '', label: 'English (default)' },
    ...[...extra, ...LOCALES].filter(isValidLocale).map((tag) => ({ value: tag, label: `${names?.of(tag) ?? tag} (${tag})` })),
  ];
}

async function timeZoneOptions(): Promise<SelectOption[]> {
  return [{ value: '', label: 'Local time of this computer' }, ...listTimeZones().map((z) => ({ value: z, label: z.replace(/_/g, ' ') }))];
}

export type ClockSettings = SettingsBag & { formatPreset: string; format: string; timeZone: string; locale: string };

class ClockRuntime extends StyledTextRuntime<ClockSettings> {
  private format = 'HH:mm:ss';
  private timeZone = '';
  private locale = '';

  constructor(ctx: SourceContext, settings: ClockSettings) {
    super(ctx);
    this.apply(settings);
  }

  update(settings: ClockSettings): void {
    if (!this.destroyed) this.apply(settings);
  }

  tick(): void {
    this.show(this.now(), { fromTick: true });
  }

  /** Redraws at least once a second only when seconds are displayed; otherwise a still picture is normal. */
  expectsContinuousFrames(): boolean {
    return this.showing && formatShowsSeconds(this.format);
  }

  protected rerender(): void {
    this.show(this.now(), { force: true });
  }

  /** Derived from the wall clock at every tick, never from counting ticks, so a hidden tab cannot drift it. */
  private now(): string {
    return formatClock(new Date(), this.format, this.timeZone, this.locale);
  }

  private apply(settings: ClockSettings): void {
    const preset = str(settings.formatPreset, 'custom');
    this.format = (preset === 'custom' ? str(settings.format, 'HH:mm:ss') : preset) || 'HH:mm:ss';
    const tz = str(settings.timeZone, '').trim();
    const zoneOk = !tz || isValidTimeZone(tz);
    this.timeZone = zoneOk ? tz : '';
    this.noteWarning('zone', zoneOk ? null : `Unknown time zone "${tz}"; showing this computer's local time.`);
    const locale = str(settings.locale, '').trim();
    const localeOk = isValidLocale(locale);
    this.locale = localeOk ? locale : '';
    this.noteWarning('locale', localeOk ? null : `Unknown language "${locale}"; showing English names.`);
    this.applyStyle(settings, this.now());
    this.show(this.now(), { force: true });
  }
}

export const clockDefinition: SourceTypeDefinition<ClockSettings> = {
  kind: 'clock',
  displayName: 'Clock',
  caps: { video: true, audio: false },
  category: 'graphics',
  settingsVersion: 1,
  migrate: (raw) => {
    const s = (raw && typeof raw === 'object' ? { ...(raw as SettingsBag) } : {}) as ClockSettings;
    // Before presets the format was always free text; keep it as the custom format.
    if (typeof s.formatPreset !== 'string') s.formatPreset = 'custom';
    if (typeof s.format !== 'string') s.format = 'HH:mm:ss';
    if (typeof s.timeZone !== 'string') s.timeZone = '';
    if (typeof s.locale !== 'string') s.locale = '';
    return s;
  },
  defaults: () => ({
    ...TEXT_STYLE_DEFAULTS,
    tabularDigits: true,
    fontAssetId: '',
    formatPreset: 'HH:mm:ss',
    format: 'HH:mm:ss',
    timeZone: '',
    locale: '',
  }),
  properties: (): PropertyDescriptor[] => [
    { key: 'formatPreset', label: 'Display', type: 'select', options: CLOCK_FORMAT_PRESETS },
    {
      key: 'format',
      label: 'Custom format',
      type: 'text',
      description: 'Tokens: YYYY YY MMMM MMM MM M DD D dddd ddd HH H hh h mm m ss s SSS A a. Use h or hh with A for a 12-hour clock; [text] is literal.',
      visibleWhen: (s) => s.formatPreset === 'custom',
    },
    { key: 'timeZone', label: 'Time zone', type: 'select', searchable: true, options: timeZoneOptions },
    { key: 'locale', label: 'Language of names', type: 'select', searchable: true, options: localeOptions() },
    { key: 'tabularDigits', label: 'Fixed-width digits', type: 'boolean', description: 'Stops the text moving as digits change.' },
    ...textStyleProperties(),
  ],
  create: (ctx, settings) => new ClockRuntime(ctx, settings),
};

// ---------------------------------------------------------------------------
// countdown
// ---------------------------------------------------------------------------

/**
 * What a finished countdown shows: `text` the finished text (zeros when it is
 * empty), `hold` zeros, `hide` nothing, `loop` starts the duration over at
 * once (in `until` mode, which has no length to repeat, it holds zeros).
 */
export type CountdownEnding = 'text' | 'hold' | 'hide' | 'loop';

export type CountdownSettings = SettingsBag & {
  mode: 'duration' | 'until';
  durationMs: number;
  targetIso: string;
  format: string;
  finishedText: string;
  ending: CountdownEnding;
};

class CountdownRuntime extends StyledTextRuntime<CountdownSettings> {
  private mode: 'duration' | 'until' = 'duration';
  private durationMs = 300_000;
  private targetIso = '';
  private format = 'mm:ss';
  private finishedText = '';
  private ending: CountdownEnding = 'text';
  private state: CountdownState = resetCountdown(300_000);

  constructor(ctx: SourceContext, settings: CountdownSettings, runtimeState?: SettingsBag) {
    super(ctx);
    this.apply(settings, runtimeState ?? null);
  }

  update(settings: CountdownSettings): void {
    if (!this.destroyed) this.apply(settings, undefined);
  }

  tick(): void {
    this.refresh(true);
  }

  expectsContinuousFrames(): boolean {
    if (!this.showing || !formatShowsSeconds(this.format)) return false;
    if (this.mode === 'until') return (this.untilRemaining() ?? 0) > 0;
    return this.state.running && !countdownFinished(this.state, Date.now(), this.loopMs());
  }

  invoke(action: string): void {
    if (this.destroyed || this.mode !== 'duration') return;
    const now = Date.now();
    if (action === 'start' || action === 'resume') this.state = startCountdown(this.state, now, this.durationMs);
    else if (action === 'pause') this.state = pauseCountdown(this.state, now, this.loopMs());
    else if (action === 'toggle') {
      this.state = this.state.running && !countdownFinished(this.state, now, this.loopMs())
        ? pauseCountdown(this.state, now, this.loopMs())
        : startCountdown(this.state, now, this.durationMs);
    } else if (action === 'reset') this.state = resetCountdown(this.durationMs);
    else return;
    this.persist();
    this.refresh(false);
  }

  protected override liveDetail(): SourceStatusDetail | undefined {
    if (this.mode !== 'duration' || this.state.running) return undefined;
    return this.state.remainingAtStart < this.durationMs ? 'paused' : 'stopped';
  }

  protected rerender(): void {
    this.refresh(false, true);
  }

  private loopMs(): number {
    return this.ending === 'loop' ? this.durationMs : 0;
  }

  private untilRemaining(): number | null {
    const target = Date.parse(this.targetIso);
    return Number.isNaN(target) ? null : Math.max(0, target - Date.now());
  }

  private persist(): void {
    this.ctx.saveRuntimeState(this.mode === 'duration' ? countdownToBag(this.state, this.durationMs) : null);
  }

  private refresh(fromTick: boolean, force = false): void {
    if (this.destroyed) return;
    let remaining: number;
    let finished: boolean;
    if (this.mode === 'until') {
      const left = this.untilRemaining();
      if (left === null) {
        this.shown = null;
        this.clearVideo();
        this.fail('unknown', this.targetIso.trim() ? `"${this.targetIso}" is not a valid date and time.` : 'Choose the date and time to count down to.');
        return;
      }
      remaining = left;
      finished = left <= 0;
    } else {
      const now = Date.now();
      remaining = countdownRemaining(this.state, now, this.loopMs());
      finished = countdownFinished(this.state, now, this.loopMs());
    }
    let text: string | null = formatDuration(remaining, this.format);
    if (finished && this.ending === 'text' && this.finishedText) text = this.finishedText;
    else if (finished && this.ending === 'hide') text = null;
    this.show(text, { fromTick, force: force || (!fromTick && this.getStatus().state === 'error') });
  }

  private apply(settings: CountdownSettings, restored: SettingsBag | null | undefined): void {
    const mode = settings.mode === 'until' ? 'until' : 'duration';
    const durationMs = Math.round(num(settings.durationMs, 300_000, 0, 31_536_000_000));
    const initial = restored !== undefined;
    if (initial) {
      this.state = (mode === 'duration' ? countdownFromBag(restored, durationMs) : null) ?? resetCountdown(durationMs);
    } else if (mode !== this.mode || durationMs !== this.durationMs) {
      // A different length is a different timer: it stops at the new full length.
      this.state = resetCountdown(durationMs);
      this.mode = mode;
      this.durationMs = durationMs;
      this.persist();
    }
    this.mode = mode;
    this.durationMs = durationMs;
    this.targetIso = str(settings.targetIso, '').trim();
    this.format = str(settings.format, 'mm:ss') || 'mm:ss';
    this.finishedText = str(settings.finishedText, '');
    const ending = str(settings.ending, 'text');
    this.ending = ending === 'hold' || ending === 'hide' || ending === 'loop' ? ending : 'text';
    this.applyStyle(settings, formatDuration(this.durationMs, this.format) + this.finishedText);
    this.refresh(false, true);
  }
}

export const countdownDefinition: SourceTypeDefinition<CountdownSettings> = {
  kind: 'countdown',
  displayName: 'Countdown',
  caps: { video: true, audio: false },
  category: 'graphics',
  settingsVersion: 1,
  migrate: (raw) => {
    const s = (raw && typeof raw === 'object' ? { ...(raw as SettingsBag) } : {}) as CountdownSettings;
    // Before `ending` existed a non-empty finished text replaced the digits and an empty one held zero.
    if (typeof s.ending !== 'string') s.ending = typeof s.finishedText === 'string' && s.finishedText ? 'text' : 'hold';
    return s;
  },
  defaults: () => ({
    ...TEXT_STYLE_DEFAULTS,
    tabularDigits: true,
    fontAssetId: '',
    mode: 'duration',
    durationMs: 300_000,
    targetIso: '',
    format: 'mm:ss',
    finishedText: 'Starting now',
    ending: 'text',
  }),
  properties: (): PropertyDescriptor[] => [
    {
      key: 'mode',
      label: 'Mode',
      type: 'select',
      options: [
        { value: 'duration', label: 'Fixed duration' },
        { value: 'until', label: 'Until a date and time' },
      ],
    },
    { key: 'durationMs', label: 'Duration', type: 'duration', min: 0, max: 31_536_000_000, visibleWhen: (s) => s.mode !== 'until' },
    { key: 'targetIso', label: 'Count down to', type: 'datetime', visibleWhen: (s) => s.mode === 'until' },
    { key: 'start', label: 'Start', type: 'button', action: 'start', variant: 'primary', visibleWhen: (s) => s.mode !== 'until' },
    { key: 'pause', label: 'Pause', type: 'button', action: 'pause', visibleWhen: (s) => s.mode !== 'until' },
    { key: 'resume', label: 'Resume', type: 'button', action: 'resume', visibleWhen: (s) => s.mode !== 'until' },
    { key: 'reset', label: 'Reset', type: 'button', action: 'reset', variant: 'danger', visibleWhen: (s) => s.mode !== 'until' },
    {
      key: 'format',
      label: 'Format',
      type: 'text',
      description: 'Tokens: D HH mm ss SSS, e.g. mm:ss or HH:mm:ss. Hours keep counting past 24 unless D is used; [text] is literal.',
    },
    {
      key: 'ending',
      label: 'When finished',
      type: 'select',
      options: [
        { value: 'text', label: 'Show the finished text' },
        { value: 'hold', label: 'Hold at zero' },
        { value: 'hide', label: 'Hide' },
        { value: 'loop', label: 'Start again (fixed duration only)' },
      ],
    },
    { key: 'finishedText', label: 'Finished text', type: 'text', description: 'Empty keeps showing zero.', visibleWhen: (s) => s.ending === 'text' || s.ending === undefined },
    { key: 'tabularDigits', label: 'Fixed-width digits', type: 'boolean', description: 'Stops the text moving as digits change.' },
    ...textStyleProperties(),
  ],
  create: (ctx, settings, runtimeState) => new CountdownRuntime(ctx, settings, runtimeState),
};

// ---------------------------------------------------------------------------
// ticker
// ---------------------------------------------------------------------------

export type TickerSettings = SettingsBag & {
  messages: ListEntry[];
  separator: string;
  speedPxPerSec: number;
  /** 0 follows the canvas width. */
  width: number;
  gapPx: number;
  direction: 'left' | 'right';
  paused: boolean;
};

/** The message texts in order: the `messages` list, or the single `text` of settings older than lists. */
export function tickerMessages(settings: SettingsBag): string[] {
  const list = settings.messages;
  if (Array.isArray(list)) {
    return list
      .map((e) => (e && typeof e === 'object' && typeof (e as ListEntry).value === 'string' ? (e as ListEntry).value : ''))
      .map((v) => v.replace(/\r?\n/g, ' '))
      .filter((v) => v.trim() !== '');
  }
  const text = str(settings.text, '').replace(/\r?\n/g, ' ');
  return text.trim() ? [text] : [];
}

let entrySeq = 0;
function newEntryId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `m${Date.now().toString(36)}${(entrySeq++).toString(36)}`;
}

class TickerRuntime extends TextSourceRuntime<TickerSettings> {
  private readonly out = document.createElement('canvas');
  private readonly strip = document.createElement('canvas');
  private speed = 120;
  private widthSetting = 0;
  private width = 1280;
  private gap = 80;
  private direction: 'left' | 'right' = 'left';
  private paused = false;
  private text = '';
  /** Layout size of the rendered strip (the canvas is this times the raster ratio). */
  private stripSize = { width: 0, height: 0 };
  /** Scroll position in layout px, 0 ≤ offset < period. */
  private offset = 0;

  constructor(ctx: SourceContext, settings: TickerSettings) {
    super(ctx);
    this.apply(settings);
  }

  update(settings: TickerSettings): void {
    if (!this.destroyed) this.apply(settings);
  }

  expectsContinuousFrames(): boolean {
    return this.showing && !this.paused && this.speed > 0 && this.stripSize.width > 0;
  }

  tick(time: FrameTime): void {
    if (this.destroyed) return;
    if (this.widthSetting === 0 && this.ctx.getVideoSettings().width !== this.width) {
      this.build();
      return;
    }
    if (this.paused || this.stripSize.width === 0 || this.speed === 0 || !this.getVideo()) return;
    const period = this.period();
    const travel = (this.speed * time.deltaMs) / 1000;
    this.offset = (((this.offset + (this.direction === 'left' ? travel : -travel)) % period) + period) % period;
    this.paint();
    this.touchVideo();
  }

  protected rerender(): void {
    this.build();
  }

  private period(): number {
    return this.stripSize.width + this.gap;
  }

  private apply(settings: TickerSettings): void {
    this.speed = num(settings.speedPxPerSec, 120, 0, 10000);
    this.widthSetting = Math.round(num(settings.width, 0, 0, 8192));
    this.gap = Math.round(num(settings.gapPx, 80, 0, 8192));
    this.direction = settings.direction === 'right' ? 'right' : 'left';
    this.paused = settings.paused === true;
    this.text = tickerMessages(settings).join(str(settings.separator, '   •   '));
    this.applyStyle(settings, this.text);
    this.build();
  }

  /** Renders the strip once (transparent; paint() lays the background down once); ticks only blit it. */
  private build(): void {
    if (this.destroyed) return;
    const oldPeriod = this.period();
    this.width = this.widthSetting || this.ctx.getVideoSettings().width;
    let size: { width: number; height: number } | null;
    if (this.text) {
      size = this.render(this.strip, this.text, { wrapWidth: 0, boxWidth: 0, boxHeight: 0, backgroundColor: '' });
      if (!size) return;
    } else {
      // Nothing to scroll: keep a strip of the text height so the background box stays visible.
      size = this.render(this.strip, ' ', { wrapWidth: 0, boxWidth: 0, boxHeight: 0, backgroundColor: '' });
      if (!size) return;
      size = { width: 0, height: size.height };
    }
    this.stripSize = size;
    // A live edit keeps the strip where it is: same phase of the new period.
    const period = this.period();
    this.offset = oldPeriod > 0 && period > 0 ? ((this.offset / oldPeriod) * period) % period : 0;
    const fit = rasterFit(this.width, size.height, this.ratio);
    const resized = this.out.width !== fit.canvasWidth || this.out.height !== fit.canvasHeight;
    if (this.out.width !== fit.canvasWidth) this.out.width = fit.canvasWidth;
    if (this.out.height !== fit.canvasHeight) this.out.height = fit.canvasHeight;
    this.paint();
    const video = this.getVideo();
    if (resized || !video || video.width !== this.width || video.height !== size.height) this.publishVideo(this.out, this.width, size.height, false, fit.ratio);
    else this.touchVideo();
    this.setStatus('live', undefined, this.paused ? 'paused' : undefined);
  }

  private paint(): void {
    const ctx = this.out.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.out.width, this.out.height);
    const height = this.stripSize.height;
    ctx.setTransform(this.out.width / this.width, 0, 0, this.out.height / height, 0, 0);
    if (this.style.backgroundColor) {
      ctx.fillStyle = this.style.backgroundColor;
      ctx.fillRect(0, 0, this.width, height);
    }
    const period = this.period();
    if (this.stripSize.width > 0) {
      // Fractional positions: the strip is resampled between pixels instead of jumping whole pixels.
      for (let x = -this.offset; x < this.width; x += period) {
        if (x + this.stripSize.width > 0) ctx.drawImage(this.strip, x, 0, this.stripSize.width, height);
      }
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  protected override release(): void {
    super.release();
    this.out.width = 0;
    this.out.height = 0;
    this.strip.width = 0;
    this.strip.height = 0;
  }
}

export const tickerDefinition: SourceTypeDefinition<TickerSettings> = {
  kind: 'ticker',
  displayName: 'Scrolling Ticker',
  caps: { video: true, audio: false },
  category: 'graphics',
  settingsVersion: 1,
  migrate: (raw) => {
    const s = (raw && typeof raw === 'object' ? { ...(raw as SettingsBag) } : {}) as TickerSettings & { text?: unknown };
    // Version 0 had one `text` string; it becomes the first and only message.
    if (typeof s.text === 'string') {
      s.messages = s.text.trim() ? [{ id: newEntryId(), value: s.text }] : [];
      delete s.text;
    }
    if (!Array.isArray(s.messages)) s.messages = [];
    if (typeof s.separator !== 'string') s.separator = '   •   ';
    if (typeof s.paused !== 'boolean') s.paused = false;
    return s;
  },
  defaults: () => ({
    ...TEXT_STYLE_DEFAULTS,
    fontAssetId: '',
    fontSize: 48,
    backgroundColor: '#000000a0',
    padding: 8,
    messages: [{ id: newEntryId(), value: 'Breaking news scrolls across here' }],
    separator: '   •   ',
    speedPxPerSec: 120,
    width: 0,
    gapPx: 120,
    direction: 'left',
    paused: false,
  }),
  properties: (): PropertyDescriptor[] => [
    { key: 'messages', label: 'Messages', type: 'list', item: 'text', itemLabel: 'Message', reorderable: true, allowDuplicates: true },
    { key: 'separator', label: 'Separator', type: 'text', description: 'Shown between messages.' },
    { key: 'speedPxPerSec', label: 'Speed', type: 'number', min: 0, max: 10000, step: 5, unit: 'px/s' },
    { key: 'paused', label: 'Paused', type: 'boolean' },
    { key: 'width', label: 'Width', type: 'number', min: 0, max: 8192, step: 1, unit: 'px', description: '0 matches the canvas width.' },
    { key: 'gapPx', label: 'Gap between repeats', type: 'number', min: 0, max: 8192, step: 1, unit: 'px' },
    {
      key: 'direction',
      label: 'Direction',
      type: 'select',
      options: [
        { value: 'left', label: 'Right to left' },
        { value: 'right', label: 'Left to right' },
      ],
    },
    ...textStyleProperties().filter((p) => !['wrapWidth', 'align', 'boxWidth', 'boxHeight', 'valign'].includes(p.key)),
  ],
  create: (ctx, settings) => new TickerRuntime(ctx, settings),
};
